'use client'

import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Info, X } from 'lucide-react'
import { toast } from 'react-toastify'
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog'
import {
  useGetSalesChannelsQuery,
  useReplaceListingSalesChannelsMutation,
} from 'graphql/generated/graphql'
import { resolveSalesChannels } from '@/lib/salesChannels'
import { cn } from '@/lib/utils'

type SalesChannelItem = {
  sales_channel_id: number
  name?: string | null
}

type HowToBuyEditModalProps = {
  open: boolean
  onClose: () => void
  listingId: number
  selected: SalesChannelItem[]
  onSaved: () => void
}

export default function HowToBuyEditModal({
  open,
  onClose,
  listingId,
  selected,
  onSaved,
}: HowToBuyEditModalProps) {
  const { data, loading } = useGetSalesChannelsQuery({ fetchPolicy: 'cache-and-network' })
  const [replaceSalesChannels, { loading: saving }] = useReplaceListingSalesChannelsMutation()
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Reset from the listing's current channels each time the modal opens, not on
  // every parent re-render -- selected is a fresh array reference each time the
  // listingDetail query refetches, which would otherwise stomp in-progress edits.
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selected.map((s) => s.sales_channel_id)))
    }
  }, [open, selected])

  const channels = useMemo(
    () => resolveSalesChannels(data?.getSalesChannels),
    [data?.getSalesChannels]
  )

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSave = async () => {
    const withIds = channels.filter(
      (c) => c.sales_channel_id != null && selectedIds.has(c.sales_channel_id)
    )
    if (selectedIds.size > 0 && withIds.length === 0) {
      toast.error('Sales channels are still loading. Please try again in a moment.')
      return
    }
    try {
      const input = withIds.map((c) => ({
        sales_channel_id: c.sales_channel_id!,
        name: c.name,
      }))
      await replaceSalesChannels({ variables: { listingId, input } })
      toast.success('How to buy updated')
      onSaved()
    } catch (err) {
      console.error('Failed to save sales channels:', err)
      toast.error('Failed to save. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/[0.35] backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[1000] flex flex-col overflow-hidden bg-white duration-200',
            // Mobile: near-full sheet; desktop: centered 960 card
            'inset-x-2 bottom-2 top-auto max-h-[92dvh] w-auto rounded-2xl p-4 shadow-[0px_0px_25px_rgba(0,0,0,0.15)]',
            'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90vh] sm:w-[min(960px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl sm:p-6 md:p-8'
          )}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-shrink-0 items-center justify-between gap-3 pb-3 sm:pb-4">
              <DialogTitle className="font-inter text-xl font-semibold leading-[29px] text-[#202020] sm:text-2xl">
                How to Buy
              </DialogTitle>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center text-[#6D6D6D] hover:text-[#202020]"
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {loading && !data?.getSalesChannels?.length ? (
                <div className="flex h-40 items-center justify-center text-sm text-gray-500">
                  Loading…
                </div>
              ) : (
                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {channels.map((channel) => {
                    const canSelect = channel.sales_channel_id != null
                    const isSelected =
                      canSelect && selectedIds.has(channel.sales_channel_id as number)
                    return (
                      <button
                        key={channel.id}
                        type="button"
                        disabled={!canSelect}
                        onClick={() => canSelect && toggle(channel.sales_channel_id as number)}
                        className={`flex min-h-[132px] w-full min-w-0 flex-col items-center justify-center gap-3 rounded-xl p-3 text-center transition-colors disabled:cursor-wait disabled:opacity-70 sm:min-h-[153px] sm:gap-4 ${
                          isSelected ? 'bg-[#E5F3D4]' : 'bg-[#F7F7F7] hover:bg-[#ECECEC]'
                        }`}
                      >
                        {channel.icon && (
                          <Image
                            src={channel.icon}
                            alt=""
                            width={48}
                            height={48}
                            className="h-10 w-10 flex-shrink-0 object-contain sm:h-12 sm:w-12"
                            unoptimized
                          />
                        )}
                        <div className="flex w-full min-w-0 flex-col items-center gap-1">
                          <p
                            className={`w-full font-inter text-sm font-semibold leading-[19px] sm:text-base ${
                              isSelected ? 'text-[#265C0D]' : 'text-[#202020]'
                            }`}
                          >
                            {channel.name}
                          </p>
                          <p className="w-full font-inter text-xs font-normal leading-[150%] text-[#202020] sm:text-sm">
                            {channel.description}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}

              <div className="mt-3 flex w-full items-start gap-3 rounded-lg border-[1.5px] border-[#F3B514] bg-[#FDF8EC] p-3 sm:mt-4 sm:items-center">
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#F3B514] sm:mt-0" strokeWidth={2} />
                <p className="min-w-0 font-inter text-sm font-medium leading-6 tracking-[-0.01em] text-[#202020] sm:text-base">
                  Select every way customers can buy from you. You can choose more than one.
                </p>
              </div>
            </div>

            <div className="flex flex-shrink-0 items-center justify-end gap-4 border-t border-[#E6E6E6] pt-3 sm:gap-7 sm:border-0 sm:pt-4">
              <button
                type="button"
                onClick={onClose}
                className="font-inter text-base font-normal leading-[30px] text-[#202020]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex h-11 min-w-[102px] items-center justify-center rounded-full bg-[#6EAC36] px-6 py-3 font-inter text-base font-medium leading-[19px] text-white disabled:opacity-70 sm:h-[43px] sm:px-8"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
